import React, { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import {
    Bar,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { publicApi } from "../../api/http";
import "./PlayerDraftOddsTab.css";

function parseDraftOddsResponse(data) {
    if (typeof data === "string") {
        return JSON.parse(data);
    }
    return Array.isArray(data) ? data : [];
}

function buildChartData(oddsRows) {
    const sorted = [...oddsRows].sort((a, b) => a.pick - b.pick);
    let cumulativeTaken = 0;

    return sorted.map(({ pick, odds }) => {
        const available = Math.max(0, 1 - cumulativeTaken);
        cumulativeTaken += odds;
        return {
            pick,
            round: Math.ceil(pick / 32),
            selected: odds,
            available,
        };
    });
}

function DraftOddsTooltip({ active, payload, label }) {
    if (!active || !payload?.length) {
        return null;
    }

    const point = payload[0]?.payload;
    const available = point?.available ?? 0;
    const selected = point?.selected ?? 0;

    return (
        <div className="draft-odds-tooltip">
            <div className="draft-odds-tooltip-title">
                Overall pick {label} · Round {point?.round}
            </div>
            <div className="draft-odds-tooltip-row">
                <span>Still available</span>
                <strong>{(available * 100).toFixed(1)}%</strong>
            </div>
            <div className="draft-odds-tooltip-row">
                <span>Selected here</span>
                <strong>{(selected * 100).toFixed(1)}%</strong>
            </div>
        </div>
    );
}

function PlayerDraftOddsTab({ playerId, draftYear, isActive }) {
    const [draftOdds, setDraftOdds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!isActive || !playerId || !draftYear) {
            return undefined;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);

        publicApi
            .get(`/draft_odds/player_id/${playerId}`, { params: { draft_year: draftYear } })
            .then((response) => {
                if (cancelled) return;
                setDraftOdds(parseDraftOddsResponse(response.data));
            })
            .catch((err) => {
                if (cancelled) return;
                console.error("Error fetching draft odds:", err);
                setDraftOdds([]);
                setError("Unable to load draft odds.");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [isActive, playerId, draftYear]);

    const chartData = useMemo(() => buildChartData(draftOdds), [draftOdds]);

    const summary = useMemo(() => {
        if (!chartData.length) {
            return null;
        }

        const totalProb = chartData.reduce((sum, row) => sum + row.selected, 0);
        const expectedPick =
            totalProb > 0
                ? chartData.reduce((sum, row) => sum + row.pick * row.selected, 0) / totalProb
                : 0;
        const pFirst = chartData.find((row) => row.pick === 1)?.selected ?? 0;
        const pTop10 = chartData
            .filter((row) => row.pick <= 10)
            .reduce((sum, row) => sum + row.selected, 0);
        const pTop32 = chartData
            .filter((row) => row.pick <= 32)
            .reduce((sum, row) => sum + row.selected, 0);

        return {
            expectedPick,
            pFirst,
            pTop10,
            pTop32,
        };
    }, [chartData]);

    return (
        <Box className="player-tab-panel draft-odds-tab-panel" hidden={!isActive}>
            {!draftYear ? (
                <Typography variant="body1" component="div" className="empty-tab-message">
                    Draft odds are not available for this player.
                </Typography>
            ) : (
                <>
                    <Typography variant="h4" component="div" className="tab-section-title">
                        Draft Odds
                    </Typography>
                    <Typography variant="body2" component="div" className="draft-odds-tab-subtitle">
                        Simulated pick probabilities for the {draftYear} NHL Draft.
                    </Typography>

                    {loading && (
                        <Box className="draft-odds-loading">
                            <CircularProgress size={28} />
                        </Box>
                    )}

                    {!loading && error && (
                        <Typography variant="body1" component="div" className="empty-tab-message">
                            {error}
                        </Typography>
                    )}

                    {!loading && !error && chartData.length === 0 && (
                        <Typography variant="body1" component="div" className="empty-tab-message">
                            No draft odds available for this player yet.
                        </Typography>
                    )}

                    {!loading && !error && chartData.length > 0 && (
                        <>
                            <ResponsiveContainer width="100%" height={340}>
                                <ComposedChart
                                    data={chartData}
                                    margin={{ top: 12, right: 24, left: 8, bottom: 20 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                                    <XAxis
                                        dataKey="pick"
                                        stroke="#a1a8b3"
                                        tick={{ fill: "#a1a8b3", fontSize: 12 }}
                                        label={{
                                            value: "Overall draft pick",
                                            position: "insideBottom",
                                            offset: -8,
                                            fill: "#a1a8b3",
                                        }}
                                    />
                                    <YAxis
                                        domain={[0, 1]}
                                        stroke="#a1a8b3"
                                        tick={{ fill: "#a1a8b3", fontSize: 12 }}
                                        tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
                                        label={{
                                            value: "Probability",
                                            angle: -90,
                                            position: "insideLeft",
                                            fill: "#a1a8b3",
                                        }}
                                    />
                                    <Tooltip content={<DraftOddsTooltip />} />
                                    <Legend
                                        verticalAlign="top"
                                        wrapperStyle={{ paddingBottom: 8 }}
                                    />
                                    <Bar
                                        dataKey="selected"
                                        name="P(selected at pick)"
                                        fill="rgba(239, 118, 122, 0.72)"
                                        barSize={chartData.length > 48 ? 6 : 10}
                                        radius={[2, 2, 0, 0]}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="available"
                                        name="P(still available)"
                                        stroke="#49beaa"
                                        strokeWidth={2.5}
                                        dot={false}
                                        activeDot={{ r: 4 }}
                                    />
                                </ComposedChart>
                            </ResponsiveContainer>

                            {summary && (
                                <div className="draft-odds-summary-grid">
                                    <div className="draft-odds-summary-stat">
                                        <span className="draft-odds-summary-label">Expected pick</span>
                                        <span className="draft-odds-summary-value">{summary.expectedPick.toFixed(1)}</span>
                                    </div>
                                    <div className="draft-odds-summary-stat">
                                        <span className="draft-odds-summary-label">P(#1)</span>
                                        <span className="draft-odds-summary-value">{(summary.pFirst * 100).toFixed(1)}%</span>
                                    </div>
                                    <div className="draft-odds-summary-stat">
                                        <span className="draft-odds-summary-label">P(top 10)</span>
                                        <span className="draft-odds-summary-value">{(summary.pTop10 * 100).toFixed(1)}%</span>
                                    </div>
                                    <div className="draft-odds-summary-stat">
                                        <span className="draft-odds-summary-label">P(round 1)</span>
                                        <span className="draft-odds-summary-value">{(summary.pTop32 * 100).toFixed(1)}%</span>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </Box>
    );
}

export default PlayerDraftOddsTab;
